import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Info, Calculator } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { BottomSheet } from '../../../../components/ui/BottomSheet';
import { LABEL_STANDARD } from '../../../../lib/formStyles';
import {
  VOLT_DROP_OPTIONS,
  EARTHING_TYPE_OPTIONS,
  ZE_BY_ENQUIRY,
  PROTECTIVE_DEVICE_OPTIONS,
  DEVICE_RATING_OPTIONS,
  CABLE_TYPE_OPTIONS,
  DISCONNECTION_TIME_OPTIONS,
  INSTALLATION_METHOD_OPTIONS,
  AMBIENT_TEMP_OPTIONS,
  GROUPING_OPTIONS,
  THERMAL_INSULATION_OPTIONS,
} from './cableSizeCalcData';

const initialStep1 = {
  circuitRef: '',
  description: '',
  designCurrent: '',
  length: '',
  supplyVoltage: '',
  ze: '',
  voltDrop: '',
  earthingType: '',
  protectiveDevice: '',
  deviceRating: '',
  cableType: '',
  disconnectionTime: '',
};

const initialStep2 = {
  installationMethod: '',
  ambientTemp: '',
  grouping: '',
  thermalInsulation: '',
};

const INPUT_BASE =
  'flex h-9 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

function SelectTrigger({ label, placeholder, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${INPUT_BASE} text-left`}
    >
      <span className={label ? '' : 'text-gray-500 dark:text-gray-400'}>
        {label || placeholder}
      </span>
      <svg className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

function RadioList({ options, value, onChange, onSelect }) {
  return (
    <div className="space-y-1">
      {options.map((opt) => {
        const optVal = opt.value !== undefined && opt.value !== null ? opt.value : opt.id;
        const isChecked = value === optVal || (opt.id === '' && !value);
        return (
          <label
            key={opt.id || opt.label}
            className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
          >
            <input
              type="radio"
              name={String(opt.id)}
              value={optVal}
              checked={isChecked}
              onChange={() => {
                onChange(opt.id === '' ? '' : optVal);
                onSelect?.();
              }}
              className="h-4 w-4 text-blue-600"
            />
            <span className="text-gray-900 dark:text-gray-100">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function CableSizeCalc() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [step, setStep] = useState(1);
  const [step1, setStep1] = useState(initialStep1);
  const [step2, setStep2] = useState(initialStep2);
  const [result, setResult] = useState(null);
  const [zeModalOpen, setZeModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  // Selection sheet state
  const [openSheet, setOpenSheet] = useState(null);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';
  const homePath = isMobile ? '/tenant/mobile' : '/tenant/dashboard';

  const updateStep1 = (field, value) => setStep1((p) => ({ ...p, [field]: value }));
  const updateStep2 = (field, value) => setStep2((p) => ({ ...p, [field]: value }));

  const getLabel = (options, value, fallback = 'Select') => {
    if (!value) return fallback;
    const opt = options.find((o) => (o.value ?? o.id) === value);
    return opt?.label ?? fallback;
  };

  const handleCalculate = () => {
    const Ib = parseFloat(step1.designCurrent) || 32;
    const L = parseFloat(step1.length) || 20;
    const Uo = parseFloat(step1.supplyVoltage) || 230;
    const VdPct = step1.voltDrop ? parseFloat(step1.voltDrop) : 5;
    const Ze = parseFloat(step1.ze) || 0.35;
    const In = step1.deviceRating ? parseFloat(step1.deviceRating) : 32;
    const Ca = step2.ambientTemp ? parseFloat(step2.ambientTemp) : 30;
    const Cg = step2.grouping ? parseFloat(step2.grouping) : 1;
    const Ci = step2.thermalInsulation ?? 1;
    const discTime = step1.disconnectionTime ? parseFloat(step1.disconnectionTime) : 0.4;

    const totalCF = (1 / (Ca === 30 ? 1 : 0.97)) * (Cg === 1 ? 1 : 0.8) * Ci;
    const It = Math.ceil(Ib / totalCF);
    const R1R2 = 0.015;
    const Zs = Ze + R1R2;
    const Vd = ((2 * Ib * L * 0.018) / (It > 0 ? It : 1)) / Uo * 100;

    const cableSizes = [1, 1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95];
    const cableLabels = {
      'cu-flat-twin': 'Cu - Flat Twin & Earth',
      'cu-swa-70': 'Cu - SWA Multicore 70°C',
      'cu-swa-90': 'Cu - SWA Multicore 90°C',
      'cu-single-70': 'Cu - Single-core (singles) 70°C',
      'cu-single-90': 'Cu - Single-core 90°C thermosetting insulated cables',
      'h07rn-f': 'H07RN-F Multicore 60°C',
    };

    const cableTypeLabel = cableLabels[step1.cableType] || 'Cu - SWA Multicore 70°C';
    const minSize = cableSizes.find((s) => s * 10 >= It) || 6;

    setResult({
      cableType: cableTypeLabel,
      cableSize: minSize,
      ci: 1,
      cg: Cg === 1 ? 1 : 0.8,
      cc: 1,
      ca: Ca === 30 ? 1 : 0.97,
      totalCF: totalCF.toFixed(2),
      it: It,
      r1r2: 0.15,
      zs: Zs.toFixed(2),
      vd: Vd.toFixed(3),
      zsMax: 1.37,
      zs80: 1.1,
      params: {
        Ib,
        L,
        Uo,
        VdPct,
        earthing: getLabel(EARTHING_TYPE_OPTIONS, step1.earthingType, 'TN-C-S'),
        Ze,
        In,
        cableType: cableTypeLabel,
        discTime,
        installation: getLabel(INSTALLATION_METHOD_OPTIONS, step2.installationMethod, 'C Clipped Direct 1 Phase'),
        ambient: `${Ca} °C`,
        grouping: `${Cg} Cables`,
        insulation: getLabel(THERMAL_INSULATION_OPTIONS, step2.thermalInsulation, '0 mm'),
        protectiveDevice: getLabel(PROTECTIVE_DEVICE_OPTIONS, step1.protectiveDevice, 'BS EN 60898 MCB TYPE B'),
      },
    });
    setStep(3);
  };

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        {step > 1 ? (
          <button type="button" onClick={() => setStep(step - 1)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors" aria-label="Back">
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : (
          <Link to={backPath} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors" aria-label="Back">
            <ChevronLeft className="h-5 w-5" />
          </Link>
        )}
        <h1 className="text-lg font-semibold text-white">Cable Size Calculator</h1>
        <Link to={homePath} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors" aria-label="Home">
          <Home className="h-5 w-5" />
        </Link>
      </div>

      {/* Step indicator */}
      <div className={`my-4 py-2 px-4 rounded-lg text-center font-medium text-white ${step === 3 ? 'bg-blue-600 dark:bg-blue-700' : 'bg-red-600 dark:bg-red-700'}`}>
        {step === 3 ? 'Result' : `Step ${step}`}
      </div>

      {step === 1 && (
        <div className="space-y-4 pb-6">
          <div>
            <label className={LABEL_STANDARD}>Circuit Ref:</label>
            <input type="text" placeholder="Circuit Reference (optional)" value={step1.circuitRef} onChange={(e) => updateStep1('circuitRef', e.target.value)} className={`${INPUT_BASE} placeholder:text-gray-500 dark:placeholder:text-gray-400`} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Description</label>
            <input type="text" placeholder="Circuit Description (optional)" value={step1.description} onChange={(e) => updateStep1('description', e.target.value)} className={`${INPUT_BASE} placeholder:text-gray-500 dark:placeholder:text-gray-400`} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Design Current</label>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">Ib</span>
              <input type="number" inputMode="decimal" min="0" step="0.1" placeholder="0" value={step1.designCurrent} onChange={(e) => updateStep1('designCurrent', e.target.value)} className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none" />
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-l border-gray-300 dark:border-gray-600">Amps</span>
            </div>
          </div>
          <div>
            <label className={LABEL_STANDARD}>Length of Circuit</label>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">L</span>
              <input type="number" inputMode="decimal" min="0" step="0.1" placeholder="0" value={step1.length} onChange={(e) => updateStep1('length', e.target.value)} className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none" />
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-l border-gray-300 dark:border-gray-600">Meters</span>
            </div>
          </div>
          <div>
            <label className={LABEL_STANDARD}>Supply Voltage</label>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">Uo</span>
              <input type="number" inputMode="decimal" min="0" step="1" placeholder="230" value={step1.supplyVoltage} onChange={(e) => updateStep1('supplyVoltage', e.target.value)} className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none" />
              <span className="inline-flex items-center px-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-l border-gray-300 dark:border-gray-600">L-E Volts (AC)</span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={LABEL_STANDARD}>Supply Ze / Zdb</label>
              <button type="button" onClick={() => setZeModalOpen(true)} className="flex h-6 w-6 items-center justify-center rounded bg-blue-500 text-white text-xs font-bold hover:bg-blue-600" aria-label="Ze by enquiry">i</button>
            </div>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-600">Ze</span>
              <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.35" value={step1.ze} onChange={(e) => updateStep1('ze', e.target.value)} className="flex-1 min-w-0 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:outline-none" />
              <span className="inline-flex items-center px-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 border-l border-gray-300 dark:border-gray-600">Ohms</span>
            </div>
          </div>
          <div>
            <label className={LABEL_STANDARD}>Volt Drop (%)</label>
            <SelectTrigger label={getLabel(VOLT_DROP_OPTIONS, step1.voltDrop)} placeholder="Select" onClick={() => setOpenSheet('voltDrop')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Earthing Type</label>
            <SelectTrigger label={getLabel(EARTHING_TYPE_OPTIONS, step1.earthingType)} placeholder="Select" onClick={() => setOpenSheet('earthingType')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Protective Device</label>
            <SelectTrigger label={getLabel(PROTECTIVE_DEVICE_OPTIONS, step1.protectiveDevice)} placeholder="Select" onClick={() => setOpenSheet('protectiveDevice')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Device Rating (In)</label>
            <SelectTrigger label={getLabel(DEVICE_RATING_OPTIONS, step1.deviceRating)} placeholder="Select" onClick={() => setOpenSheet('deviceRating')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Cable Type</label>
            <SelectTrigger label={getLabel(CABLE_TYPE_OPTIONS, step1.cableType)} placeholder="Select" onClick={() => setOpenSheet('cableType')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>Disconnection Time (s)</label>
            <SelectTrigger label={getLabel(DISCONNECTION_TIME_OPTIONS, step1.disconnectionTime)} placeholder="Select" onClick={() => setOpenSheet('disconnectionTime')} />
          </div>
          <Button onClick={() => setStep(2)} className="w-full bg-green-600 hover:bg-green-700 text-white py-3">
            Next
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 pb-6">
          <div>
            <label className={LABEL_STANDARD}>Installation Method</label>
            <SelectTrigger label={getLabel(INSTALLATION_METHOD_OPTIONS, step2.installationMethod)} placeholder="Select" onClick={() => setOpenSheet('installationMethod')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>(Ca) Ambient Temperature</label>
            <SelectTrigger label={step2.installationMethod ? getLabel(AMBIENT_TEMP_OPTIONS, step2.ambientTemp) : 'Select an Installation Method First'} placeholder="Select an Installation Method First" onClick={() => step2.installationMethod && setOpenSheet('ambientTemp')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>(Cg) Grouping</label>
            <SelectTrigger label={getLabel(GROUPING_OPTIONS, step2.grouping)} placeholder="Select" onClick={() => setOpenSheet('grouping')} />
          </div>
          <div>
            <label className={LABEL_STANDARD}>(Ci) Surrounded by Thermal Insulation</label>
            <SelectTrigger label={getLabel(THERMAL_INSULATION_OPTIONS, step2.thermalInsulation)} placeholder="Select" onClick={() => setOpenSheet('thermalInsulation')} />
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            By clicking on calculate you accept our disclaimer. This cable calculation is a basic guide only, other factors may also apply, always seek expert advice.
          </p>
          <Button onClick={handleCalculate} className="w-full bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2">
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
        </div>
      )}

      {step === 3 && result && (
        <div className="space-y-4 pb-6">
          <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">
            REF: {step1.circuitRef || '1'} DESCRIPTION: {step1.description || 'test'}
          </p>
          <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 bg-teal-600 dark:bg-teal-700 text-white">
              <span className="font-medium">{result.cableType}</span>
              <span className="font-bold">{result.cableSize} mm</span>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white text-sm font-semibold">CALCULATIONS</div>
            <div className="p-4 bg-white dark:bg-gray-800 text-sm space-y-2">
              <p className="text-gray-700 dark:text-gray-300">
                CI: {result.ci} Cg: {result.cg} Cc: {result.cc} Ca: {result.ca} | Total CF: {result.totalCF}
              </p>
              <p className="text-gray-700 dark:text-gray-300">Tabulated Current (It): {result.it} Amps</p>
              <p className="text-gray-700 dark:text-gray-300">Estimated R1+R2: {result.r1r2} Ohms</p>
              <p className="text-gray-700 dark:text-gray-300">Estimated Zs: {result.zs} Ohms</p>
              <p className="text-gray-700 dark:text-gray-300">Estimated Volt Drop (Vd): {result.vd} %</p>
              <p className="text-gray-600 dark:text-gray-400 text-xs mt-2">100% Max Permitted Zs: 0.2s {result.zsMax} | 0.4s {result.zsMax} | 5s {result.zsMax}</p>
              <p className="text-gray-600 dark:text-gray-400 text-xs">80% Max Zs (used for calc): 0.2s {result.zs80} | 0.4s {result.zs80} | 5s {result.zs80}</p>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-2 bg-orange-500 dark:bg-orange-600 text-white text-sm font-semibold">YOUR DESIGN PARAMETERS</div>
            <div className="p-4 bg-white dark:bg-gray-800 text-sm space-y-1">
              <p className="text-gray-700 dark:text-gray-300">Design Current (Ib): {result.params.Ib} Amps</p>
              <p className="text-gray-700 dark:text-gray-300">Circuit Length (L): {result.params.L} Meters</p>
              <p className="text-gray-700 dark:text-gray-300">Supply Voltage (Uo): {result.params.Uo} Volts</p>
              <p className="text-gray-700 dark:text-gray-300">Max Volt Drop (Vd): {result.params.VdPct} %</p>
              <p className="text-gray-700 dark:text-gray-300">Earthing System: {result.params.earthing}</p>
              <p className="text-gray-700 dark:text-gray-300">Ze / Zdb (Ω): {result.params.Ze} Ohms</p>
              <p className="text-gray-700 dark:text-gray-300">Device Rating (In): {result.params.In} Amps</p>
              <p className="text-gray-700 dark:text-gray-300">Cable Type: {result.params.cableType}</p>
              <p className="text-gray-700 dark:text-gray-300">Max Disc. Time (s): {result.params.discTime}</p>
              <p className="text-gray-700 dark:text-gray-300">Protective Device: {result.params.protectiveDevice}</p>
              <p className="text-gray-700 dark:text-gray-300">Installation Method: {result.params.installation}</p>
              <p className="text-gray-700 dark:text-gray-300">Ambient Temperature: {result.params.ambient}</p>
              <p className="text-gray-700 dark:text-gray-300">Grouping: {result.params.grouping}</p>
              <p className="text-gray-700 dark:text-gray-300">Surrounded by Thermal Insulation: {result.params.insulation}</p>
            </div>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
            <p>This calculation is based on flat twin & earth / equal size cpc. Cable sizes meet volt drop and max Zs requirements in addition to minimum required cable rating (It). For equipment operating at lower temperatures, consult manufacturer. Larger than expected sizes may be due to Ze/Zdb or circuit length.</p>
            <p>All information and calculations are for information only. Pro Certs Software Ltd accept no liability whatsoever. You are responsible for the accuracy of data entered.</p>
          </div>
          <Button onClick={() => setInfoModalOpen(true)} variant="outline" className="w-full flex items-center justify-center gap-2">
            <Info className="h-4 w-4" />
            Info
          </Button>
        </div>
      )}

      {/* Ze by Enquiry modal */}
      <BottomSheet open={zeModalOpen} onOpenChange={setZeModalOpen} title="Ze by Enquiry">
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-4">
          <p>If you do not know the actual Ze value you may use the by enquiry values (TT example only).</p>
          <p>TT = 20 | TN-S = 0.8 | TN-C-S = 0.35</p>
          <p className="text-amber-700 dark:text-amber-400 font-medium">NOTE: For accurate calculations you should use the actual measured Ze / Zdb value at the location where you intend to supply your new circuit from.</p>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => { updateStep1('ze', ZE_BY_ENQUIRY.TT.toString()); setZeModalOpen(false); }}>20</Button>
            <Button size="sm" variant="outline" onClick={() => { updateStep1('ze', ZE_BY_ENQUIRY['TN-S'].toString()); setZeModalOpen(false); }}>0.8</Button>
            <Button size="sm" variant="outline" onClick={() => { updateStep1('ze', ZE_BY_ENQUIRY['TN-C-S'].toString()); setZeModalOpen(false); }}>0.35</Button>
            <Button size="sm" variant="outline" onClick={() => setZeModalOpen(false)}>Cancel</Button>
          </div>
        </div>
      </BottomSheet>

      {/* Info modal */}
      <BottomSheet open={infoModalOpen} onOpenChange={setInfoModalOpen} title="Cable Size Calculator Info">
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p>This calculator provides a basic guide for cable sizing based on BS 7671. It considers design current, volt drop, earth fault loop impedance (Zs), and correction factors for ambient temperature, grouping, and thermal insulation.</p>
          <p>Always verify results with on-site measurements and consult BS 7671 for full requirements.</p>
        </div>
      </BottomSheet>

      {/* Selection BottomSheets */}
      <BottomSheet open={openSheet === 'voltDrop'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Volt Drop">
        <RadioList options={VOLT_DROP_OPTIONS} value={step1.voltDrop} onChange={(v) => updateStep1('voltDrop', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'earthingType'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Earthing Type">
        <RadioList options={EARTHING_TYPE_OPTIONS} value={step1.earthingType} onChange={(v) => updateStep1('earthingType', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'protectiveDevice'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Protective Device" maxHeight="max-h-[85vh]">
        <RadioList options={PROTECTIVE_DEVICE_OPTIONS} value={step1.protectiveDevice} onChange={(v) => updateStep1('protectiveDevice', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'deviceRating'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Device Rating (In)" maxHeight="max-h-[85vh]">
        <RadioList options={DEVICE_RATING_OPTIONS} value={step1.deviceRating} onChange={(v) => updateStep1('deviceRating', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'cableType'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Cable Type" maxHeight="max-h-[85vh]">
        <RadioList options={CABLE_TYPE_OPTIONS} value={step1.cableType} onChange={(v) => updateStep1('cableType', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'disconnectionTime'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Disconnection Time">
        <RadioList options={DISCONNECTION_TIME_OPTIONS} value={step1.disconnectionTime} onChange={(v) => updateStep1('disconnectionTime', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'installationMethod'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Installation Method">
        <RadioList options={INSTALLATION_METHOD_OPTIONS} value={step2.installationMethod} onChange={(v) => updateStep2('installationMethod', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'ambientTemp'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Ambient Air Temperature">
        <RadioList options={AMBIENT_TEMP_OPTIONS} value={step2.ambientTemp} onChange={(v) => updateStep2('ambientTemp', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'grouping'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Grouping">
        <RadioList options={GROUPING_OPTIONS} value={step2.grouping} onChange={(v) => updateStep2('grouping', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
      <BottomSheet open={openSheet === 'thermalInsulation'} onOpenChange={(o) => !o && setOpenSheet(null)} title="Surrounded by Thermal Insulation">
        <RadioList options={THERMAL_INSULATION_OPTIONS} value={step2.thermalInsulation} onChange={(v) => updateStep2('thermalInsulation', v)} onSelect={() => setOpenSheet(null)} />
      </BottomSheet>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cable Size Calculator</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Calculate required cable size based on BS 7671</p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
